import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { Notice, Timeline, type TimelineItem } from "../../../design/components";
import { ApiError } from "../../../api/client";
import { dateTime } from "../../../lib/format";
import type { ProbeStep } from "../../../api/types";
import { runProbe } from "../../hub/onboard/onboardApi";
import styles from "./connection.module.css";

export type LastProbe = { at: string; ok: boolean; steps: ProbeStep[] } | null;

const NOTHING_YET = "This connection has not been tested yet.";
const STATE_BY_STATUS = { ok: "ok", failed: "fail", skipped: "warn" } as const;

/** The wizard's step list and the tab's are the same rows from the same probe. */
function toItems(steps: ProbeStep[]): TimelineItem[] {
  return steps.map((step) => ({
    title: step.title,
    at: `${step.ms} ms`,
    state: STATE_BY_STATUS[step.status] ?? "warn",
    detail: step.note,
  }));
}

export function ProbePanel({
  entityId,
  lastProbe,
  canRun,
}: {
  entityId: string;
  lastProbe: LastProbe;
  canRun: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fresh, setFresh] = useState<{ at: string; ok: boolean; steps: ProbeStep[] } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const shown = fresh ?? lastProbe;

  async function test(): Promise<void> {
    setRunning(true);
    setError("");
    try {
      const outcome = await runProbe(entityId);
      setFresh({ at: new Date().toISOString(), ok: outcome.ok, steps: outcome.steps });
      await queryClient.invalidateQueries({ queryKey: ["entities", entityId, "connection"] });
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : t("companies.probe.testError", "The test could not be run."));
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="grow">{t("companies.probe.heading", "Connection test")}</h2>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!canRun || running}
          onClick={() => void test()}
        >
          {t("companies.probe.testButton", "Test connection")}
        </button>
      </div>
      <div className="card-body">
        {shown ? (
          <p className={styles.meta}>
            {t("companies.probe.lastTestedPrefix", "Last tested {{date}} — ", { date: dateTime(shown.at) })}
            {shown.ok
              ? t("companies.probe.passed", "every step the hub could check passed.")
              : t("companies.probe.failed", "a step failed.")}
          </p>
        ) : (
          <p className={styles.meta}>{t("companies.probe.nothingYet", NOTHING_YET)}</p>
        )}
        {shown ? <Timeline items={toItems(shown.steps)} /> : null}
        {error ? <Notice kind="fail">{error}</Notice> : null}
      </div>
    </section>
  );
}
